import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { DataGateway, AccessSnapshot, SignInResult } from '@/data/types';
import type { AppNotification } from '@/domain/types';
import type { PermissionKey } from '@/domain/permissions';
import { getGateway } from '@/services/gateway';
import {
  loadSessionSnapshot,
  switchCompany as switchCompanyService,
  signOut as signOutService,
  type SessionSnapshot,
} from '@/services/session-service';
import {
  loadAllSettings,
  resolveCompanySettings,
  type ResolvedCompanySettings,
} from '@/services/settings-service';
import { listMyNotifications, sweepDueNotifications, unreadCount } from '@/services/notification-service';
import { countUnreadChatMessages, runSelectiveMediaRetention } from '@/services/operations-hub-service';
import { invalidateDataCache } from '@/app/data-cache';
import { useTheme } from './theme';

interface AuthContextValue {
  gateway: DataGateway;
  snapshot: SessionSnapshot | null;
  access: AccessSnapshot | null;
  settings: ResolvedCompanySettings | null;
  loading: boolean;
  booting: boolean;
  error: string | null;
  unread: number;
  unreadChat: number;
  /** أحدث إشعار وصل (لمشغّل الأصوات)، دون كشف بقية القائمة. */
  latestNotification: AppNotification | null;
  can: (permission: PermissionKey) => boolean;
  canAny: (permissions: PermissionKey[]) => boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
  beginPlatformSupportAccess: (companyId: string, reason: string) => Promise<void>;
  endPlatformSupportAccess: () => Promise<void>;
  refresh: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
  refreshChatUnread: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SNAPSHOT_CACHE_KEY = 'taskflow.auth.snapshot_cache';
const SETTINGS_CACHE_KEY = 'taskflow.auth.settings_cache';

function readCachedSnapshot(): SessionSnapshot | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(SNAPSHOT_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SessionSnapshot;
  } catch {
    return null;
  }
}

function writeCachedSnapshot(snapshot: SessionSnapshot | null) {
  try {
    if (typeof localStorage === 'undefined') return;
    if (snapshot) {
      localStorage.setItem(SNAPSHOT_CACHE_KEY, JSON.stringify(snapshot));
    } else {
      localStorage.removeItem(SNAPSHOT_CACHE_KEY);
    }
  } catch {
    // Ignore storage quota
  }
}

function readCachedSettings(): ResolvedCompanySettings | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ResolvedCompanySettings;
  } catch {
    return null;
  }
}

function writeCachedSettings(settings: ResolvedCompanySettings | null) {
  try {
    if (typeof localStorage === 'undefined') return;
    if (settings) {
      localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settings));
    } else {
      localStorage.removeItem(SETTINGS_CACHE_KEY);
    }
  } catch {}
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const gateway = useMemo(() => getGateway(), []);
  const { applyBranding } = useTheme();
  const location = useLocation();
  // مساحة المنصة تطبّق سمة المنصة وحدها. أي تحميل جلسة أو تحديث خلفي أثناء
  // التواجد فيها يجب ألا يعيد سمة الشركة فوقها.
  const onPlatformRoute = location.pathname === '/platform' || location.pathname.startsWith('/platform/');
  const onPlatformRouteRef = useRef(onPlatformRoute);
  onPlatformRouteRef.current = onPlatformRoute;
  const initialSnapshot = useMemo(() => readCachedSnapshot(), []);
  const initialSettings = useMemo(() => readCachedSettings(), []);
  const [snapshot, setSnapshot] = useState<SessionSnapshot | null>(() => initialSnapshot);
  const [settings, setSettings] = useState<ResolvedCompanySettings | null>(() => initialSettings);
  const [loading, setLoading] = useState(() => !initialSnapshot);
  const [booting, setBooting] = useState(() => !initialSnapshot);
  const [error, setError] = useState<string | null>(null);
  const [unread, setUnread] = useState(0);
  const [unreadChat, setUnreadChat] = useState(0);
  const [latestNotification, setLatestNotification] = useState<AppNotification | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const applyNotifications = useCallback((notifications: AppNotification[]) => {
    if (!mounted.current) return;
    setUnread(unreadCount(notifications));
    const newest = notifications[0] ?? null;
    setLatestNotification((previous) =>
      previous?.id === newest?.id ? previous : newest,
    );
  }, []);

  const refreshChatUnread = useCallback(async () => {
    if (!snapshot?.activeCompanyId || !snapshot?.session?.userId) {
      setUnreadChat(0);
      return;
    }
    const currentMembership = snapshot.memberships.find(
      (m) => m.companyId === snapshot.activeCompanyId,
    );
    try {
      const counts = await countUnreadChatMessages(
        gateway,
        snapshot.activeCompanyId,
        snapshot.session.userId,
        currentMembership?.primaryBranchId,
      );
      if (mounted.current) setUnreadChat(counts.total);
    } catch {
      // ignore
    }
  }, [gateway, snapshot?.activeCompanyId, snapshot?.memberships, snapshot?.session?.userId]);

  const refreshNotifications = useCallback(async () => {
    if (snapshot?.platformSupportSession) {
      setUnread(0);
      setLatestNotification(null);
      return;
    }
    if (!snapshot?.activeCompanyId) {
      setUnread(0);
      return;
    }
    try {
      const notifications = await listMyNotifications(gateway, snapshot.activeCompanyId, 50);
      applyNotifications(notifications);
    } catch {
      // لا نُظهر خطأً للعدّاد؛ الصفحة تعرض الخطأ عند فتح الإشعارات
    }
    void refreshChatUnread();
  }, [applyNotifications, gateway, refreshChatUnread, snapshot?.activeCompanyId, snapshot?.platformSupportSession]);

  const load = useCallback(
    async (mode: 'initial' | 'refresh' = 'refresh') => {
      if (mode === 'initial') {
        if (!initialSnapshot) setBooting(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const next = await loadSessionSnapshot(gateway);
        if (!mounted.current) return;
        writeCachedSnapshot(next);
        writeCachedSettings(null);
        invalidateDataCache();
        setSnapshot(next);
        if (mode === 'initial') {
          setBooting(false);
        }
        if (next?.activeCompanyId) {
          const membership = next.platformSupportSession ? undefined : next.memberships.find(
            (entry) => entry.companyId === next.activeCompanyId,
          );
          const records = await loadAllSettings(gateway);
          const branchId = membership?.primaryBranchId ?? null;
          let brandId: string | null = null;
          if (branchId) {
            try {
              const links = await gateway.table('brand_branch_links').list({
                filter: { branchId: { op: 'eq', value: branchId } },
              });
              brandId = links.find((link) => link.isPrimary)?.brandId ?? links[0]?.brandId ?? null;
            } catch {
              // لا نُفشل الدخول بسبب تعذّر قراءة ربط العلامة.
            }
          }
          const resolved = resolveCompanySettings(records, next.activeCompanyId, {
            userId: next.session.userId,
            branchId,
            brandId,
          });
          writeCachedSettings(resolved);
          setSettings(resolved);
          if (!onPlatformRouteRef.current) {
            applyBranding(resolved.theme, undefined, 'company');
          }

          // المهام الخلفية الثقيلة تجري بدون تعطيل واجهة المستخدم إطلاقًا
          const activeCompanyId = next.activeCompanyId;
          const currentUserId = next.session.userId;
          void (async () => {
            if (!next.platformSupportSession) {
              try {
                if (resolved.completedTaskRetention.autoDeleteEnabled) {
                  await gateway.commands.purgeCompletedTasks({
                    companyId: activeCompanyId,
                    actorUserId: currentUserId,
                    olderThanDays: Math.max(1, resolved.completedTaskRetention.autoDeleteAfterDays || 7),
                    dryRun: false,
                  });
                }
              } catch (_) {}
              try {
                if (resolved.mediaHubPolicy?.autoDeleteEnabled) {
                  await runSelectiveMediaRetention(
                    gateway,
                    {
                      userId: currentUserId,
                      companyId: activeCompanyId,
                      membershipId: membership?.id ?? null,
                    },
                    resolved.mediaHubPolicy,
                    { dryRun: false },
                  );
                }
              } catch (_) {}
              try {
                await sweepDueNotifications(
                  gateway,
                  activeCompanyId,
                  currentUserId,
                  Math.max(1, Math.round(resolved.notifications.dueSoonLeadMinutes / 60)),
                );
              } catch (_) {}
              try {
                const notifications = await listMyNotifications(gateway, activeCompanyId, 50);
                applyNotifications(notifications);
              } catch (_) {}
            } else {
              applyNotifications([]);
            }
          })();
        } else {
          setSettings(null);
          setUnread(0);
          setLatestNotification(null);
        }
      } catch (cause) {
        if (mounted.current) {
          setError(cause instanceof Error ? cause.message : 'تعذّر تحميل الجلسة');
        }
      } finally {
        if (mounted.current) {
          setBooting(false);
          setLoading(false);
        }
      }
    },
    [applyBranding, applyNotifications, gateway],
  );
  useEffect(() => {
    void load('initial');
    const unsubscribe = gateway.auth.onAuthStateChange(() => {
      void load('refresh');
    });
    return unsubscribe;
  }, [gateway, load]);

  // تحديث دوري للعدّاد؛ التذكيرات تُولّد على الخادم عند توفر Supabase.
  useEffect(() => {
    if (!snapshot) return;
    const timer = window.setInterval(() => void refreshNotifications(), 30_000);
    return () => window.clearInterval(timer);
  }, [snapshot, refreshNotifications]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await gateway.auth.signIn(email, password);
      await load('refresh');
      return result;
    },
    [gateway, load],
  );

  const signOut = useCallback(async () => {
    const supportSession = gateway.getPlatformSupportSession
      ? await gateway.getPlatformSupportSession().catch(() => null)
      : null;
    if (supportSession && gateway.endPlatformSupportAccess) {
      await gateway.endPlatformSupportAccess(supportSession.id);
    }
    await signOutService(gateway);
    writeCachedSnapshot(null);
    writeCachedSettings(null);
    invalidateDataCache();
    if (mounted.current) {
      setSnapshot(null);
      setSettings(null);
      setUnread(0);
      setLatestNotification(null);
    }
  }, [gateway]);

  const switchCompany = useCallback(
    async (companyId: string) => {
      if (!snapshot) return;
      setLoading(true);
      try {
        const next = await switchCompanyService(
          gateway,
          snapshot.session.userId,
          companyId,
          snapshot.memberships,
        );
        writeCachedSnapshot(next);
        writeCachedSettings(null);
        invalidateDataCache();
        setSnapshot(next);
        if (next?.activeCompanyId) {
          const membership = next.memberships.find(
            (entry) => entry.companyId === next.activeCompanyId,
          );
          const records = await loadAllSettings(gateway);
          const branchId = membership?.primaryBranchId ?? null;
          let brandId: string | null = null;
          if (branchId) {
            try {
              const links = await gateway.table('brand_branch_links').list({
                filter: { branchId: { op: 'eq', value: branchId } },
              });
              brandId = links.find((link) => link.isPrimary)?.brandId ?? links[0]?.brandId ?? null;
            } catch {
              // لا نُفشل الدخول بسبب تعذّر قراءة ربط العلامة.
            }
          }
          let resolved = resolveCompanySettings(records, next.activeCompanyId, {
            userId: next.session.userId,
            branchId,
            brandId,
          });
          if (!next.platformSupportSession && resolved.completedTaskRetention.autoDeleteEnabled) {
            try {
              const purged = await gateway.commands.purgeCompletedTasks({
                companyId: next.activeCompanyId,
                actorUserId: next.session.userId,
                olderThanDays: Math.max(1, resolved.completedTaskRetention.autoDeleteAfterDays || 7),
                dryRun: false,
              });
              if (purged.tasksCount > 0) {
                const refreshedRecords = await loadAllSettings(gateway);
                resolved = resolveCompanySettings(refreshedRecords, next.activeCompanyId, {
                  userId: next.session.userId,
                  branchId,
                  brandId,
                });
              }
            } catch {
              // التنظيف التلقائي لا يعطّل تحميل الجلسة
            }
          }
          if (!next.platformSupportSession && resolved.mediaHubPolicy?.autoDeleteEnabled) {
            void runSelectiveMediaRetention(
              gateway,
              {
                userId: next.session.userId,
                companyId: next.activeCompanyId,
                membershipId: membership?.id ?? null,
              },
              resolved.mediaHubPolicy,
              { dryRun: false },
            ).catch(() => undefined);
          }
          setSettings(resolved);
          if (!onPlatformRouteRef.current) {
            applyBranding(resolved.theme, undefined, 'company');
          }
          const notifications = await listMyNotifications(gateway, next.activeCompanyId, 50);
          applyNotifications(notifications);
        }
      } finally {
        setLoading(false);
      }
    },
    [applyBranding, applyNotifications, gateway, snapshot],
  );

  const beginPlatformSupportAccess = useCallback(async (companyId: string, reason: string) => {
    if (!gateway.beginPlatformSupportAccess) throw new Error('دخول الدعم متاح عند الاتصال بقاعدة المنصة فقط');
    await gateway.beginPlatformSupportAccess({ companyId, reason });
    await load('refresh');
  }, [gateway, load]);
  const endPlatformSupportAccess = useCallback(async () => {
    const supportSession = snapshot?.platformSupportSession ?? (gateway.getPlatformSupportSession ? await gateway.getPlatformSupportSession() : null);
    if (!supportSession || !gateway.endPlatformSupportAccess) throw new Error('لا توجد جلسة دعم نشطة لإنهائها');
    await gateway.endPlatformSupportAccess(supportSession.id);
    await load('refresh');
  }, [gateway, load, snapshot?.platformSupportSession]);
  const access = snapshot?.access ?? null;
  const permissionSet = useMemo(() => new Set<PermissionKey>(access?.permissions ?? []), [access]);

  const can = useCallback(
    (permission: PermissionKey) => permissionSet.has(permission),
    [permissionSet],
  );
  const canAny = useCallback(
    (permissions: PermissionKey[]) => permissions.some((permission) => permissionSet.has(permission)),
    [permissionSet],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      gateway,
      snapshot,
      access,
      settings,
      loading,
      booting,
      error,
      unread,
      unreadChat,
      latestNotification,
      can,
      canAny,
      signIn,
      signOut,
      switchCompany,
      beginPlatformSupportAccess,
      endPlatformSupportAccess,
      refresh: () => load('refresh'),
      refreshNotifications,
      refreshChatUnread,
    }),
    [
      gateway,
      snapshot,
      access,
      settings,
      loading,
      booting,
      error,
      unread,
      unreadChat,
      latestNotification,
      can,
      canAny,
      signIn,
      signOut,
      switchCompany,
      beginPlatformSupportAccess,
      endPlatformSupportAccess,
      load,
      refreshNotifications,
      refreshChatUnread,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

/**
 * هوية التنفيذ الحالية (عضوية الشركة النشطة).
 *
 * مُذاكرة بالقيمة لأن الكائن الجديد في كل رسم كان يسبب حلقات تحديث لا نهائية
 * في الصفحات التي تعتمد على الهوية داخل useCallback.
 */
export function useActor(): { userId: string; membershipId: string | null; companyId: string; isPlatformSupport: boolean } | null {
  const { snapshot } = useAuth();
  return useMemo(() => {
    if (!snapshot?.activeCompanyId) return null;
    if (snapshot.platformSupportSession && snapshot.access?.isPlatformSupport) {
      return {
        userId: snapshot.session.userId,
        membershipId: null,
        companyId: snapshot.activeCompanyId,
        isPlatformSupport: true,
      };
    }
    const membership = snapshot.memberships.find(
      (entry) => entry.companyId === snapshot.activeCompanyId && entry.status === 'active',
    );
    if (!membership) return null;
    return {
      userId: snapshot.session.userId,
      membershipId: membership.id,
      companyId: snapshot.activeCompanyId,
      isPlatformSupport: false,
    };
  }, [snapshot]);
}
